import { action, makeObservable, observable } from 'mobx';
import type { TFn } from '@/i18n/LocaleProvider';
import { invitationJoinedNotice } from './invitationJoinedNoticeStore';
import {
  forgetPendingInvitation,
  signUpInvitation,
  signUpInvitationPlace,
} from './pendingInvitation';

/**
 * F-0584 MWI-28 (I-2170): the first sign-in after an account was created from an invitation link on
 * this device. The sign-up spent the link's use and the address's confirmation wrote its grants, so
 * there is no Join left to press: this asks the link's answer once (`POST /invitations/redeem`, which
 * answers a person who redeemed it already and spends nothing), says "You joined A and B." with a line
 * per place skipped, and names the first place granted as `target`, which the shell opens, as an
 * accept does.
 *
 * `pending` holds the first open (I-1992) until the answer is in, so All work areas is never drawn
 * first. A link the server refuses by then (revoked since) is forgotten, and the sign-in lands as any
 * other does.
 *
 * The invite page's arithmetic and the API client are read when a sign-up link is held, never at
 * launch: this module is in `Layout`'s entry, and `invitationModel` loads with its screens (gate 707).
 */
type LandingStateType = 'idle' | 'asking' | 'done';

export interface ISignUpLandingDeps {
  t: TFn;
  /**
   * The work area granted first, entered before its address opens, so the first open (I-1992) finds
   * a place of this person's and never sends them on to All work areas. Not called for an
   * organisation or a guest.
   */
  enter: (workAreaId: string) => void;
  /** The person's work areas read again, so the sidebar names the places joined. */
  refreshWorkAreas: () => void;
}

export class SignUpInvitationLanding {
  state: LandingStateType = 'idle';
  /**
   * Where the answer opens, until the address is there. The shell redirects to it rather than this
   * navigating once: a redirect the sign-in's own address still had queued (`/` to `/-/items`)
   * replaced a single navigation and the first open then went on to All work areas (measured).
   */
  target: string | null = null;

  constructor() {
    makeObservable(this, {
      state: observable,
      target: observable,
      settle: action,
      startAsking: action,
      arrived: action,
    });
  }

  /** Whether the first open waits: a sign-up link is held and its answer is not in yet. */
  get pending(): boolean {
    if (this.state === 'asking') return true;
    return this.state === 'idle' && signUpInvitation() !== null;
  }

  /** The address is the target's: nothing more to open. */
  arrived(): void {
    this.target = null;
  }

  async land({ t, enter, refreshWorkAreas }: ISignUpLandingDeps): Promise<void> {
    if (this.state !== 'idle') return;
    const token = signUpInvitation();
    if (!token) {
      this.settle();
      return;
    }
    this.startAsking();
    try {
      const [{ accessControlApi }, model] = await Promise.all([
        import('@/services/AccessControlApi'),
        import('./invitationModel'),
      ]);
      const place = signUpInvitationPlace();
      const joined = place
        ? await accessControlApi.redeemInvitation(token, place)
        : await accessControlApi.redeemInvitation(token);
      forgetPendingInvitation();
      const granted = joined.granted ?? [];
      invitationJoinedNotice.show({
        places: model.namesInWords(
          granted.length > 0
            ? granted.map((place) => place.scopeLabel ?? t('invitations.pending.joinedAPlace'))
            : [joined.scopeLabel ?? t('invitations.pending.joinedAPlace')],
          t,
        ),
        skipped: (joined.skipped ?? []).map((place) => model.skippedPlaceLine(place, t)),
        agency: joined.agency ?? null,
      });
      refreshWorkAreas();
      const first = granted[0];
      if (!joined.guest && first?.scopeType === 'work_area') enter(first.scopeId);
      this.settle(model.redemptionDestination(joined));
    } catch (error) {
      // Principle review 31: refused, or unreadable, it says why and what to do; the link is not
      // asked again.
      forgetPendingInvitation();
      invitationJoinedNotice.show({ places: '', skipped: [], refusal: t(refusalKeyOf(error)) });
      this.settle();
    }
  }

  startAsking(): void {
    this.state = 'asking';
  }

  settle(target: string | null = null): void {
    this.target = target;
    this.state = 'done';
  }
}

/**
 * Principle review 31: which sentence an invitation the sign-up could not open reads — gone (ran out,
 * used or withdrawn), sent to another address, or not opened just now (offline, a server error).
 */
export function refusalKeyOf(
  error: unknown,
):
  | 'common.invitationJoined.refused.gone'
  | 'common.invitationJoined.refused.otherAddress'
  | 'common.invitationJoined.refused.failed' {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  if (status === 400 || status === 404 || status === 410)
    return 'common.invitationJoined.refused.gone';
  if (status === 403) return 'common.invitationJoined.refused.otherAddress';
  return 'common.invitationJoined.refused.failed';
}

export const signUpInvitationLanding = new SignUpInvitationLanding();
